/**
 * useAuthReady Hook
 *
 * Tracks whether Firebase auth has finished initializing.
 * Used for splash screen timing - shows splash until auth resolves.
 *
 * Returns:
 * - isReady: true when Firebase auth state has been determined
 * - user: Firebase User object (or null)
 * - isAuthenticated: true if user is logged in
 */

import { useState, useEffect } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '@/firebase/config';

interface AuthState {
  isReady: boolean;       // Firebase has resolved (not still loading)
  user: User | null;      // Current user or null
  isAuthenticated: boolean;
}

export function useAuthReady(): AuthState {
  const [isReady, setIsReady] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setIsReady(true);
    });

    return unsubscribe;
  }, []);

  return {
    isReady,
    user,
    isAuthenticated: !!user,
  };
}
